import { useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'

const menuItems = [
  { path: '/', label: 'Dashboard', icon: '📊', permission: 'dashboard' },
  { type: 'divider', label: 'Validasi File' },
  { path: '/reviewer', label: 'Review File', icon: '🔎', permission: 'review_file' },
  { path: '/upload-file', label: 'Upload File', icon: '📤', permission: 'files' },
  { path: '/file-recap', label: 'Rekap File', icon: '📑', permission: 'review_file' },
  { type: 'divider', label: 'Produksi' },
  { path: '/station', label: 'Update Stasiun', icon: '📱', permission: 'station_update' },
  { path: '/comcolor', label: 'Cetak Comcolor', icon: '⚡', permission: 'cetak_comcolor' },
  { path: '/edit-qc', label: 'Edit Riwayat QC', icon: '✏️', permission: 'edit_qc' },
  { path: '/spk', label: 'Manajemen SPK', icon: '📝', permission: 'spk' },
  { path: '/queue', label: 'Antrian Produksi', icon: '📋', permission: 'queue' },
  { path: '/warehouse', label: 'Gudang', icon: '📦', permission: 'warehouse' },
  { path: '/reports', label: 'Laporan', icon: '📈', permission: 'reports' },
  { type: 'divider', label: 'Master Data' },
  { path: '/operators', label: 'Operator', icon: '👷', permission: 'station_update' },
  { path: '/books', label: 'Buku', icon: '📚', permission: 'spk_management' },
  { path: '/schools', label: 'Sekolah', icon: '🏫', permission: 'master_sekolah' },
  { path: '/vendors', label: 'Vendor', icon: '🏭', permission: 'vendor' },
  { path: '/racks', label: 'Rak Gudang', icon: '🗄️', permission: 'warehouse' },
  { type: 'divider', label: 'Admin' },
  { path: '/users', label: 'Pengguna', icon: '👥', permission: 'users' },
  { path: '/roles', label: 'Hak Akses', icon: '🔐', permission: 'roles' },
  { path: '/api-management', label: 'API Eksternal', icon: '🔗', permission: 'api_management' },
]

function Layout() {
  const { user, logout, hasPermission } = useAuth()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [userMenuOpen, setUserMenuOpen] = useState(false)

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const filteredMenu = menuItems.filter(item => {
    if (item.type === 'divider') return true
    return !item.permission || hasPermission(item.permission)
  })

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gray-900 text-white transition-all duration-300 flex flex-col`}>
        {/* Logo */}
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🖨️</span>
            {sidebarOpen && (
              <div>
                <h1 className="font-bold text-lg">SIM Percetakan</h1>
                <span className="text-xs text-gray-400">v8.15</span>
              </div>
            )}
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1.5 rounded hover:bg-gray-700 transition"
          >
            {sidebarOpen ? '◀' : '▶'}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {filteredMenu.map((item, idx) => {
            if (item.type === 'divider') {
              return sidebarOpen ? (
                <div key={idx} className="px-4 py-2 mt-4 mb-1">
                  <span className="text-xs text-gray-500 uppercase tracking-wider">{item.label}</span>
                </div>
              ) : (
                <div key={idx} className="border-t border-gray-700 my-2 mx-2" />
              )
            }

            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`
                }
                title={item.label}
              >
                <span className="text-xl">{item.icon}</span>
                {sidebarOpen && <span>{item.label}</span>}
              </NavLink>
            )
          })}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-gray-700">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{user?.avatar || '👤'}</span>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user?.name}</p>
                <p className="text-xs text-gray-400 truncate">{user?.role_name}</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              INFINITE & NAJMI
            </h2>
            <p className="text-sm text-gray-500">Sistem Informasi Manajemen Produksi Percetakan</p>
          </div>

          <div className="flex items-center gap-4">
            {/* Notifications */}
            <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg relative">
              <span className="text-xl">🔔</span>
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <span className="text-xl">{user?.avatar || '👤'}</span>
                <span className="text-sm font-medium">{user?.name}</span>
                <span className="text-xs">▼</span>
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
                  <button
                    onClick={() => {
                      setUserMenuOpen(false)
                      navigate('/profile')
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                  >
                    👤 Profil Saya
                  </button>
                  <button
                    onClick={() => {
                      setUserMenuOpen(false)
                      handleLogout()
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100"
                  >
                    🚪 Keluar
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="bg-white border-t px-6 py-3 text-center text-sm text-gray-500">
          &copy; 2024 INFINITE & NAJMI - SIM Percetakan v8.15
        </footer>
      </div>

      {/* Click outside to close user menu */}
      {userMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setUserMenuOpen(false)}
        />
      )}
    </div>
  )
}

export default Layout
